import { useState, useEffect, useRef } from "react";
import type { VideoJob, VideoJobCreate } from "../lib/types";
import { getVideoJob } from "../lib/api";
import { saveFile, isIOS } from "../lib/download";

// Matches H3 Studio's chip labels.
const QUALITY_LABEL: Record<string, string> = {
  fast: "Fast", quality: "High", "2k": "2K",
};

interface VideoJobCardProps {
  video: VideoJob;
  parentJobId: string;
  onDelete: (videoJobId: string) => void;
  onReload: (config: VideoJobCreate) => void;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

function VideoProcessingAnimation() {
  return (
    <div className="flex items-center gap-3">
      <div className="processing-spinner" />
      <span className="text-xs text-indigo-300">Generating video…</span>
    </div>
  );
}

export function VideoJobCard({ video, parentJobId, onDelete, onReload }: VideoJobCardProps) {
  const [currentVideo, setCurrentVideo] = useState<VideoJob>(video);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Keep in sync with parent updates
  useEffect(() => {
    setCurrentVideo(video);
  }, [video]);

  // Fetch presigned URL when complete (the list endpoint doesn't include videoUrl)
  useEffect(() => {
    if (currentVideo.status === "complete" && !currentVideo.videoUrl && currentVideo.outputKey) {
      getVideoJob(parentJobId, currentVideo.videoJobId)
        .then((fresh) => setCurrentVideo(fresh))
        .catch((err) => console.error("Failed to get video URL:", err));
    }
  }, [currentVideo.status, currentVideo.videoUrl, currentVideo.outputKey, parentJobId, currentVideo.videoJobId]);

  const handleDelete = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setConfirmingDelete(false);
    onDelete(currentVideo.videoJobId);
  };

  const handleDownload = async () => {
    if (!currentVideo.videoUrl || downloading) return;
    setDownloading(true);
    try {
      await saveFile(
        currentVideo.videoUrl,
        `chatter-video-${currentVideo.videoJobId}.mp4`,
        "video/mp4",
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleReload = () => {
    onReload({
      prompt: currentVideo.prompt,
      aspect: currentVideo.aspect ?? "16:9",
      quality: currentVideo.quality ?? "fast",
      gpu: currentVideo.gpu,
      steps: currentVideo.steps,
      seed: currentVideo.seed,
      // Older LTX-era rows only ever had a single image.
      refImageKeys: currentVideo.refImageKeys?.length
        ? currentVideo.refImageKeys
        : currentVideo.sourceImageKey
          ? [currentVideo.sourceImageKey]
          : [],
      refVideoKeys: currentVideo.refVideoKeys ?? [],
      audioStartS: currentVideo.audioStartS,
      takeIndex: currentVideo.takeIndex,
      // Presigned by the read endpoints so the panel can repaint the tiles
      // instead of reopening with an empty reference list.
      refImageUrls: currentVideo.refImageUrls,
      refVideoUrls: currentVideo.refVideoUrls,
      refImageLabels: currentVideo.refImageLabels,
      refVideoLabels: currentVideo.refVideoLabels,
      scene: currentVideo.scene,
    });
  };

  // Rows created before the H3 switch carry the old LTX fields instead.
  const summary = currentVideo.quality
    ? [
        QUALITY_LABEL[currentVideo.quality] ?? currentVideo.quality,
        currentVideo.aspect,
        currentVideo.gpu,
      ]
        .filter(Boolean)
        .join(" · ")
    : [currentVideo.qualityMode, currentVideo.resolution,
       currentVideo.fps ? `${currentVideo.fps}fps` : null]
        .filter(Boolean)
        .join(" · ");

  const isActive = currentVideo.status === "queued" || currentVideo.status === "processing";

  return (
    <div className={`rounded-xl border px-3 py-2.5 space-y-2 ${
      isActive
        ? "border-indigo-500/20 bg-indigo-500/5 card-processing"
        : currentVideo.status === "failed"
          ? "border-red-500/20 bg-red-500/5"
          : "border-slate-700/50 bg-slate-800/30"
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm">🎬</span>
          <span className="text-xs text-slate-400 truncate max-w-[180px]">
            {currentVideo.prompt}
          </span>
        </div>
        <span className="text-[10px] text-slate-600 shrink-0">
          {summary}
        </span>
      </div>

      {/* Status content */}
      {isActive && <VideoProcessingAnimation />}

      {currentVideo.status === "failed" && (
        <p className="text-xs text-red-400">
          Failed: {currentVideo.error ?? "Unknown error"}
        </p>
      )}

      {currentVideo.status === "complete" && currentVideo.videoUrl && (
        <div className="space-y-2">
          <video
            ref={videoRef}
            src={currentVideo.videoUrl}
            controls
            preload="metadata"
            className="w-full rounded-lg max-h-48 bg-slate-900"
          />
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="btn-ghost py-1 px-2 text-xs"
              title={isIOS() ? "Save or share" : "Download"}
            >
              {downloading ? "…" : isIOS() ? "⬆ Save" : "⬇ Download"}
            </button>
            <button
              onClick={handleReload}
              className="btn-ghost py-1 px-2 text-xs"
              title="Reload settings"
            >
              🔁 Reload
            </button>
            <button
              onClick={handleDelete}
              onBlur={() => setConfirmingDelete(false)}
              className={`text-xs ml-auto ${confirmingDelete ? "btn-danger ring-2 ring-red-400" : "btn-danger"}`}
            >
              {confirmingDelete ? "Confirm?" : "Delete"}
            </button>
          </div>
          {currentVideo.durationS && (
            <p className="text-[10px] text-slate-600">{formatDuration(currentVideo.durationS)}</p>
          )}
        </div>
      )}

      {/* Delete button for non-complete states */}
      {!isActive && currentVideo.status !== "complete" && (
        <div className="flex justify-end">
          <button
            onClick={handleDelete}
            onBlur={() => setConfirmingDelete(false)}
            className={`text-xs ${confirmingDelete ? "btn-danger ring-2 ring-red-400" : "btn-danger"}`}
          >
            {confirmingDelete ? "Confirm?" : "Delete"}
          </button>
        </div>
      )}
    </div>
  );
}
